<template>
  <div class="rounded-xl mt-4 menu-card">
    <div class="tab-bar" ref="tabBarRef">
      <v-tabs class="d-inline-block" v-model="tab" color="var(--color-primary)" @update:modelValue="handleTabChange">
        <v-tab v-for="menu in filteredMenus" :key="menu.value" @mouseenter="onMouseEnter(menu.value)" :value="menu.value">{{
          menu.label }}</v-tab>
      </v-tabs>
      <div class="tab-bar-hot">
      </div>
      <div class="floating-tab-bar" :style="{ transform: `translateX(${floatTabBarLeft}px)` }" ref="floatingTabBarRef"
        v-show="getChildren(activeTab).length > 0" @mouseleave="onMouseLeaveTab">
        <div class="tab-buttons">
          <button v-for="child in getChildren(activeTab)" :key="child.value"
            :class="[subTab === child.value ? 'active' : '']" @click="handleSubTabClick(child.value);">
            {{ child.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { computed, ref, useTemplateRef, onMounted, nextTick, watch } from 'vue'
import { storeToRefs } from 'pinia'
import useUserStore from '@/store/modules/user'
const tabBarRef = useTemplateRef('tabBarRef')
const floatingTabBarRef = useTemplateRef('floatingTabBarRef')
const userStore = useUserStore()
const { permissions } = storeToRefs(userStore)

const props = defineProps({
  menus: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['tab-change', 'sub-tab-change', 'change'])
const tab = defineModel('modelValue')
const subTab = defineModel('sub-tab')


const activeTab = ref('config')
const floatTabBarLeft = ref(0)

function hasPerms(perms) {
  if (!Array.isArray(perms) || !perms.length) return true
  return (permissions.value || []).some(permission => permission === '*:*:*' || perms.includes(permission))
}

const filteredMenus = computed(() => {
  void permissions.value
  return props.menus.reduce((out, menu) => {
    if (!hasPerms(menu.perms)) return out
    const next = { ...menu }
    if (Array.isArray(menu.children) && menu.children.length) {
      next.children = menu.children.filter(child => hasPerms(child.perms))
    }
    out.push(next)
    return out
  }, [])
})

async function onMouseEnter(tab) {
  activeTab.value = tab
  // 确保floatingTabBarRef已经渲染
  await nextTick()
  const index = filteredMenus.value.findIndex(menu => menu.value === tab)
  const el = getElement(index)
  if (!el) {
    return
  }
  const localPosition = getLocalPosition(el);
  let left = localPosition.centerX - floatingTabBarRef.value.clientWidth / 2
  floatTabBarLeft.value = Math.max(left, 0)
}


function handleTabChange(path) {
  emit('tab-change', path)
  emit('change', path, '')
}

function handleSubTabClick(path) {
  if (path === subTab.value) {
    return
  }
  subTab.value = path
  emit('sub-tab-change', path, activeTab.value)
  emit('change', activeTab.value, path)
}

function getLocalPosition(el) {
  const tabBarRect = tabBarRef.value.getBoundingClientRect()
  const rect = el.getBoundingClientRect()
  return {
    left: rect.left - tabBarRect.left,
    top: rect.top - tabBarRect.top,
    right: rect.right - tabBarRect.left,
    centerX: rect.left + rect.width / 2 - tabBarRect.left,
  }
}

function onMouseLeaveTab() {
  activeTab.value = ''
}

function getChildren(tab) {
  return filteredMenus.value.find(menu => menu.value === tab)?.children || []
}


function getElement(index) {
  if (!tabBarRef.value) {
    return null
  }
  const tabElements = Array.from(tabBarRef.value.querySelector('.v-slide-group__content').children)
  return tabElements.at(index)
}

watch(filteredMenus, (menus) => {
  if (!menus.length) return
  const current = menus.find(menu => menu.value === tab.value)
  if (!current) {
    tab.value = menus[0].value
    subTab.value = menus[0].children?.[0]?.value
    emit('change', tab.value, subTab.value || '')
    return
  }
  if (current.children?.length && subTab.value && !current.children.some(child => child.value === subTab.value)) {
    subTab.value = current.children[0].value
    emit('change', tab.value, subTab.value)
  }
}, { immediate: true, flush: 'post' })


</script>




<style scoped lang="scss">
.menu-card {
  border-radius: 30px 30px 0 0 !important;
  background-color: #fff !important;
  border-bottom: 1px solid #EFF4FB !important;
}

.tab-bar {
  // box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
  position: relative;
  width: fit-content;

  &:hover {
    .floating-tab-bar {
      display: block;
    }
  }

  :deep(.v-tabs) {
    position: relative;
    z-index: 2;
  }
}

.tab-bar-hot {
  position: absolute;
  inset: 0;
  bottom: -2px;
  // background-color: #003cff34;
  z-index: 1;
}

.floating-tab-bar {
  display: none;
  position: absolute;
  top: 50px;
  left: 0;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.tab-buttons {
  display: flex;
  gap: 10px;
}

.tab-buttons button {
  padding: 8px 16px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.tab-buttons button:hover {
  background-color: #e6f0ff;
  border-color: #2655f1;
  color: #2655f1;
}

.tab-buttons button.active {
  background-color: #f7f8ff;
  border-color: #2655f1;
  color: #2655F1;
}
</style>
